import React, { useState } from 'react';
import Navbar from './Navbar/NavBar';
import { 
  Box, 
  Toolbar, 
  IconButton, 
  ThemeProvider, 
  Menu, 
  MenuItem, 
  ListItemText, 
  Typography, 
  Button 
} from '@mui/material';
import { drawerWidth } from '../Utils/constants';
import useIsDesktop from '../Hooks/useIsDesktop';
import BlitzHeader from './BlitzHeader';
import MenuIcon from '@mui/icons-material/Menu';
import KeyboardArrowLeftSharpIcon from '@mui/icons-material/KeyboardArrowLeftSharp';
import KeyboardArrowRightSharpIcon from '@mui/icons-material/KeyboardArrowRightSharp';
import CloseIcon from '@mui/icons-material/Close';
import { lightTheme } from '../Utils/baseTheme';

const openedMixin = (theme) => ({
  minWidth: {
    xs: '100%',
    md: `calc(100% - ${drawerWidth})`,
  },
  marginInlineStart: {
    xs: 0,
    md: drawerWidth,
  },
  transition: theme.transitions.create(['min-width', 'margin-inline-start'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
});

const closedMixin = (theme) => ({
  transition: theme.transitions.create(['min-width', 'margin-inline-start'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  minWidth: {
    xs: '100%',
    md: `calc(100% - ${lightTheme.spacing(8)} + 1px)`,
  },
  marginInlineStart: {
    xs: 0,
    md: `calc(${lightTheme.spacing(8)} - 1px)`,
  },
});

const BlitzLayout = ({ children, containerStyle = {}, onHelp }) => {
  const isDesktop = useIsDesktop();
  const [isOpen, setIsOpen] = useState(isDesktop);
  const [mobileActions, setMobileActions] = useState([]);
  const [notificationAnchor, setNotificationAnchor] = useState(null);
  const isExtended = (!isDesktop || isOpen);

  const handleGetMobileActions = (actions) => setMobileActions(actions);

  // Menu button - desktop gets toggle arrows, mobile gets nothing (no hamburger)
  const MenuButton = isDesktop ? (
    <IconButton
      edge="start"
      onClick={() => setIsOpen(prev => !prev)}
      sx={{ color: '#fff', mr: 2 }}
    >
      {isOpen ? (
        <KeyboardArrowLeftSharpIcon />
      ) : (
        <KeyboardArrowRightSharpIcon />
      )}
    </IconButton>
  ) : null;

  // Get notification data from mobile actions
  const notificationAction = mobileActions.find(action => action.key === 'notifications');

  return (
    <>
      <ThemeProvider theme={lightTheme}>
        <Navbar
          isDesktop={isDesktop}
          isOpen={isOpen}
          onClose={() => setIsOpen(false)}
          onClickHelp={onHelp}
          onOpen={() => setIsOpen(true)}
          onGetMobileActions={handleGetMobileActions}
          notificationAnchor={notificationAnchor}
          onSetNotificationAnchor={setNotificationAnchor}
        />
        
        {/* Show header ONLY on mobile */}
        {!isDesktop && (
          <>
            <BlitzHeader
              menuButton={MenuButton}
              isDesktop={isDesktop}
              mobileActions={mobileActions}
            />
            <Toolbar />
          </>
        )}

        {/* External Notifications Menu - works for both desktop and mobile */}
        {notificationAction && (
          <Menu
            anchorEl={notificationAnchor}
            open={notificationAction.notifMenuOpen}
            onClose={notificationAction.handleCloseNotifications}
            anchorOrigin={{ 
              vertical: "bottom", 
              horizontal: isDesktop ? "left" : "right" 
            }}
            transformOrigin={{ 
              vertical: "top", 
              horizontal: isDesktop ? "left" : "right" 
            }}
            PaperProps={{
              sx: {
                maxHeight: 400,
                width: 320,
                '& .MuiMenuItem-root': {
                  whiteSpace: 'normal',
                  padding: 2,
                }
              }
            }}
          >
            {/* Header with close button */}
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              px: 2,
              py: 1,
              borderBottom: '1px solid rgba(0,0,0,0.12)'
            }}>
              <Typography variant="h6">Notifications</Typography>
              <IconButton 
                size="small" 
                onClick={notificationAction.handleCloseNotifications}
                sx={{ ml: 1 }}
              >
                <CloseIcon />
              </IconButton>
            </Box>
            
            {!notificationAction.notificationsData?.length && (
              <MenuItem disabled>
                <ListItemText primary="No notifications" />
              </MenuItem>
            )}
            
            {notificationAction.notificationsData?.map((notif) => {
              const isUnread = !notif.read_at;
              return (
                <MenuItem
                  key={notif.id}
                  onClick={() => notificationAction.handleNotificationClick(notif)}
                  sx={{
                    backgroundColor: isUnread ? "#f2f2f2" : "#fff",
                    borderBottom: '1px solid rgba(0,0,0,0.06)',
                    '&:hover': {
                      backgroundColor: isUnread ? "#e8e8e8" : "#f5f5f5",
                    }
                  }}
                >
                  <ListItemText
                    primary={notif.purpose}
                    secondary={notif.message}
                    primaryTypographyProps={{
                      fontWeight: isUnread ? 'bold' : 'normal',
                      fontSize: '0.9rem'
                    }}
                    secondaryTypographyProps={{
                      fontSize: '0.8rem'
                    }}
                  />
                  {isUnread && (
                    <Box sx={{ 
                      width: 8, 
                      height: 8, 
                      borderRadius: '50%', 
                      backgroundColor: 'secondary.main',
                      ml: 1
                    }} />
                  )}
                </MenuItem>
              );
            })}
            
            {notificationAction.notificationsData?.length > 0 && (
              <Box sx={{ p: 1, textAlign: 'center', borderTop: '1px solid rgba(0,0,0,0.12)' }}>
                <Button 
                  size="small" 
                  onClick={notificationAction.handleCloseNotifications}
                  sx={{ textTransform: 'none' }}
                >
                  Close
                </Button>
              </Box>
            )}
          </Menu>
        )}
      </ThemeProvider>
      
      <Box
        id='TopPageContainer'
        sx={(theme) => ({
          ...containerStyle,
          ...(isExtended ? openedMixin(theme) : closedMixin(theme)),
        })}
      >
        {children}
      </Box>
    </>
  );
};

export default BlitzLayout;